/**
 * AdminManufacturersEnhanced - Enhanced Manufacturers list view with standardized table
 */
import React, { useState, useCallback } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  BooleanCell,
  BulkActionToolbar,
  TruncatedCell,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  BooleanFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
} from '../../components/admin/BulkActionModals';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { manufacturersAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Edit,
  Trash2,
  Factory,
  Package,
  ExternalLink,
  AlertTriangle,
  Plus,
  Mail,
  Phone,
  Globe,
} from 'lucide-react';

const AdminManufacturersEnhanced = () => {
  const { isAdmin } = useAuth();
  
  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMfr, setEditingMfr] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    contact_email: '',
    phone: '',
    notes: ''
  });
  
  // Fetch function for manufacturers
  const fetchManufacturers = useCallback(async (params) => {
    return await manufacturersAPI.getManufacturers(params);
  }, []);
  
  // Use admin table hook
  const {
    data: manufacturers,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchManufacturers,
    defaultSortBy: 'name',
    defaultSortDir: 'asc',
    defaultPageSize: 25,
    filterKeys: ['is_active'],
    transformResponse: (data) => ({
      items: data.manufacturers || data.items || [],
      total: data.total || 0
    })
  });
  
  // Get blocked items for delete (those with products)
  const getBlockedDeleteItems = () => {
    return manufacturers
      .filter(m => selectedIds.includes(m.id))
      .filter(m => (m.product_count || 0) > 0)
      .map(m => ({
        id: m.id,
        reason: `${m.name} has ${m.product_count} associated products`
      }));
  };
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await manufacturersAPI.bulkDelete({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} manufacturer(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} manufacturer(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete manufacturers');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Handle edit
  const handleEdit = (mfr) => {
    setEditingMfr(mfr);
    setFormData({
      name: mfr.name || '',
      website: mfr.website || '',
      contact_email: mfr.contact_email || '',
      phone: mfr.phone || '',
      notes: mfr.notes || ''
    });
    setShowEditModal(true);
  };
  
  // Handle create new
  const handleCreate = () => {
    setEditingMfr(null);
    setFormData({
      name: '',
      website: '',
      contact_email: '',
      phone: '',
      notes: ''
    });
    setShowEditModal(true);
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingMfr) {
        await manufacturersAPI.updateManufacturer(editingMfr.id, formData);
        toast.success('Manufacturer updated!');
      } else {
        await manufacturersAPI.createManufacturer(formData);
        toast.success('Manufacturer created!');
      }
      
      setShowEditModal(false);
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to save manufacturer');
    }
  };
  
  // Table columns
  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-ivory-100 flex items-center justify-center">
            <Factory className="w-5 h-5 text-ivory-500" />
          </div>
          <div>
            <p className="font-medium text-ivory-800">{value || '—'}</p>
            {row.website && (
              <a 
                href={row.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe className="w-3 h-3" />
                Website
              </a>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'contact_email',
      header: 'Contact',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm">
          {value && (
            <div className="flex items-center gap-1 text-ivory-600">
              <Mail className="w-3 h-3" />
              <span className="truncate max-w-[150px]" title={value}>{value}</span>
            </div>
          )}
          {row.phone && (
            <div className="flex items-center gap-1 text-ivory-500 text-xs">
              <Phone className="w-3 h-3" />
              {row.phone}
            </div>
          )}
          {!value && !row.phone && <span className="text-ivory-400">—</span>}
        </div>
      )
    },
    {
      key: 'product_count',
      header: 'Products',
      sortable: true,
      align: 'center',
      render: (value) => (
        <Badge variant="outline" className="font-mono">
          <Package className="w-3 h-3 mr-1" />
          {value || 0}
        </Badge>
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: false,
      render: (value) => (
        <BooleanCell 
          value={value !== false} 
          yesLabel="Active" 
          noLabel="Inactive" 
        />
      )
    },
    {
      key: 'notes',
      header: 'Notes',
      sortable: false,
      render: (value) => (
        <TruncatedCell value={value} maxWidth={200} />
      )
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(row);
          }}
          title="Edit Manufacturer"
          data-testid={`edit-manufacturer-${row.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
      )
    }
  ];
  
  const filterLabels = {
    is_active: 'Status',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-manufacturers-enhanced">
      <AdminListHeader
        title="Manufacturers"
        subtitle={total > 0 ? `${total.toLocaleString()} total manufacturers` : undefined}
        createLabel="Add Manufacturer"
        onCreate={handleCreate}
        canCreate={isAdmin}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
      />
      
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search name, notes..."
          className="flex-1 min-w-[250px]"
        />
        
        <BooleanFilter
          value={filters.is_active}
          onChange={(v) => setFilter('is_active', v)}
          label="Status"
          trueLabel="Active"
          falseLabel="Inactive"
          allLabel="All"
          className="w-[130px]"
        />
      </FilterBar>
      
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
      />
      
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      <AdminDataTable
        tableId="manufacturers"
        data={manufacturers}
        columns={columns}
        loading={loading}
        emptyMessage="No manufacturers found"
        emptyIcon={Factory}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.id}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        density={density}
        onDensityChange={setDensity}
        showColumnSelector={true}
        resizableColumns={true}
        onRowClick={handleEdit}
        data-testid="manufacturers-data-table"
      />
      
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="manufacturer"
        loading={bulkActionLoading}
        blockedItems={getBlockedDeleteItems()}
        warnings={[
          'Manufacturers with products cannot be deleted.',
          'Remove products first or reassign them.'
        ]}
        requireReason={false}
      />
      
      {/* Edit/Create Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingMfr ? 'Edit Manufacturer' : 'Add Manufacturer'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingMfr ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManufacturersEnhanced;
